import { type SupportedLocales } from "@app/shared/utils/locales";
import {
    QuestionType,
    type GetQuestionsQuery,
} from "@generated/client/graphql";
import { useMemo } from "react";
import { useQueryParam } from "use-query-params";
import { questionnaireParamsArgs } from "../../../../ctx";
import { useGetDocuments, useQuestionAndAnswerUpdate } from "../../data";
import { useQuestionsTabTranslations } from "../i18n";

interface UseAiSuggestionConfirmProps {
    question: GetQuestionsQuery["questions"][number];
    locale: SupportedLocales;
}

export const useAiSuggestionConfirm = ({
    question,
    locale,
}: UseAiSuggestionConfirmProps) => {
    const { t, keys } = useQuestionsTabTranslations();
    const [activeGroupIdParams] = useQueryParam(
        ...questionnaireParamsArgs.group,
    );

    const {
        updateTextAnswer,
        updateSelectAnswer,
        updateEvidenceFilesAsync,
        updateValueAnswer,
        updateComment,
        updateLastInsertedDate,
    } = useQuestionAndAnswerUpdate(
        question.id,
        question.answer?.id,
        question.questionnaireId,
        {
            groupId: activeGroupIdParams,
        },
    );

    const aiSuggestion = question?.answer?.aiSuggestion;

    const { answerText, documentCitations, comment, value } = useMemo(() => {
        const documentCitations = aiSuggestion?.documentCitations ?? [];
        const success = aiSuggestion?.success;
        const comment = aiSuggestion?.comment;
        const value = aiSuggestion?.value;
        const answerText =
            typeof success === "boolean" && !success
                ? t(keys.ai_suggestion_unsuccessful)
                : question?.type === QuestionType.MultiSelect
                  ? question?.metadata?.options
                        ?.filter((_, idx) => value?.includes(idx))
                        ?.map((option) => option?.name?.[locale])
                        .join(", ")
                  : question?.type === QuestionType.SingleSelect
                    ? question?.metadata?.options?.[value]?.name?.[locale]
                    : question?.type === QuestionType.Text
                      ? value
                      : value?.toString();
        return {
            answerText,
            documentCitations,
            comment,
            value,
        };
    }, [
        question?.answer?.aiSuggestion,
        t,
        question?.type,
        question?.metadata?.options,
        locale,
    ]);

    const { documents: suggestedDocuments } = useGetDocuments(
        documentCitations?.map((c: { documentId: string }) => c.documentId) ??
            [],
    );

    const handleInsertAnswer = () => {
        if (question?.type === QuestionType.Text) {
            updateTextAnswer(answerText ?? "");
        }

        if (question?.type === QuestionType.Value) {
            updateValueAnswer(value);
        }

        if (question?.type === QuestionType.MultiSelect) {
            updateSelectAnswer(value ?? []);
        }

        if (question?.type === QuestionType.SingleSelect) {
            updateSelectAnswer([value]);
        }
        updateLastInsertedDate();
    };

    const handleConfirmClick = () => {
        // Insert the answer
        handleInsertAnswer();

        // Additional confirmation steps
        if (comment) {
            updateComment(comment);
        }

        updateEvidenceFilesAsync({
            fileIds: suggestedDocuments
                ?.map(
                    (document) => document?.lastApprovedVersion?.finalFile?.id,
                )
                .filter((fileId) => typeof fileId === "string"),
        });
    };

    const handleInsertComment = () => {
        updateComment(comment ?? "");
    };

    return {
        handleConfirmClick,
        handleInsertAnswer,
        handleInsertComment,
        answerText,
        documentCitations,
        comment,
        value,
        suggestedDocuments,
    };
};
